import { addSlideFooter } from '../slide_footer';
import { cleanQuestionTitle } from 'app/pages/Dashboard/pages/Survey/components/Dashboard/utilities/helpers.js';
import { FOOTER_TEXT, FONT_FACE, TEXT_COLOR, TITLE_TEXT } from '../constants';

/**
 * Creates text slide for open-ended questions
 * @param {Object} options
 * @param {Object} options.pptx - PptxGenJS instance
 * @param {Object} options.question - Question object
 * @param {Array} options.textSummaries - Array of text summaries from GPT API
 * @param {String} options.compareBy - Compare by setting
 * @param {String} options.reportUrl - Report URL
 * @param {String} options.reportType - Report type
 * @param {String} options.title - Survey title for source link
 * @returns {Object} Created slide or null
 */
export const createTextSlide = ({
  compareBy,
  pptx,
  question,
  questionNumber,
  reportType,
  reportUrl,
  textSummaries,
  title,
}) => {
  // Find text summaries for this question that are OPEN_END_QUESTION type
  const compareByValue = compareBy?.value;
  const questionSummaries = textSummaries?.filter((summary) => {
    const isOpenEndType =
      summary.reportType === 'OPEN_END_QUESTION' || summary.report_type === 'OPEN_END_QUESTION';
    const summaryCompareBy = summary.additionalParams?.compareBy;
    const compareByMatches =
      compareByValue === 'quota_group'
        ? summaryCompareBy === 'quota_group'
        : summaryCompareBy === 'overall';
    return isOpenEndType && compareByMatches;
  });

  if (!questionSummaries || questionSummaries.length === 0) {
    return null;
  }

  // Create one slide per summary
  questionSummaries.forEach((summary, index) => {
    // Get the text content from the output field
    const textContent = summary.output?.toString().trim();

    if (!textContent) return;

    // Clean question title
    const cleanTitle = cleanQuestionTitle(question.title || question.text);
    if (!cleanTitle) {
      return;
    }

    const slide = pptx.addSlide();

    // Create slide title with specific group name if available
    const groupName = summary.additionalParams?.compareByName;
    const compareByLabel = groupName || (compareBy === 'quota_group' ? 'Quota Group' : 'Overall');
    const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';
    const slideTitle = `${questionPrefix}${cleanTitle} Compared by: ${compareByLabel}`;

    // Use the same title options as chart slides
    const titleOptions = {
      ...TITLE_TEXT,
      breakLine: true,
      fontSize: 14,
      h: 'auto',
      shrinkText: true, // Enable automatic text shrinking
      valign: 'top',
      w: '95%',
      wrap: true,
      x: 0.5,
      y: 0.5,
    };

    slide.addText(slideTitle, titleOptions);

    // Create summary text - if text already has bullets, use as is, otherwise add summary label
    let summaryText;
    if (textContent.includes('•') || textContent.includes('\n•')) {
      summaryText = `Summary:\n${textContent}`;
    } else {
      // If no bullets, add them
      const lines = textContent.split('\n').filter((line) => line.trim());
      summaryText = 'Summary:\n' + lines.map((line) => `• ${line.trim()}`).join('\n');
    }

    if (summaryText && summaryText !== 'Summary:\n') {
      // Calculate height based on number of lines
      const lineCount = summaryText.split('\n').length;
      const textHeight = Math.min(lineCount * 0.3 + 0.5, 3.5);

      slide.addText(summaryText, {
        align: 'left',
        color: TEXT_COLOR,
        fontFace: FONT_FACE,
        fontSize: 12,
        h: textHeight,
        lineSpacing: 16,
        valign: 'top',
        w: '90%',
        wrap: true,
        x: 0.5,
        y: 1.5,
      });
    }

    addSlideFooter({ pptx, reportType, reportUrl, slide });
    slide.addText(
      [{ text: 'Source: ' }, { options: { hyperlink: { url: reportUrl } }, text: title }],
      FOOTER_TEXT
    );
  });
};
